import { Box } from "@mui/material";
import type { FC } from "react";
import { FONT_SIZE, STATUS } from "../../../../theme/tokens";
import { ColorDot } from "../../../UIToolkit/molecules/ColorDot";
import { formatStaffCount, type GapsJobTotal, isShownAsShort, shortfallOf } from "../gapsDayModel";

type Props = {
	jobTotals: GapsJobTotal[];
	/** Each job's dot colour, keyed by its label — the same map the drawer's legend is drawn from. */
	dots: ReadonlyMap<string, string>;
};

/**
 * `● −2 · ● ✓` — each job's own shortfall for the day, clamped, never netted, keyed by its colour.
 *
 * The job's NAME lives in the drawer's legend, not here: a department with six jobs printed six names
 * across a 320px line and the ellipsis cut the later ones off, so the jobs a supervisor could not see
 * were exactly the ones the line existed to report. A dot and a number always fit. The dot still names
 * its job to assistive tech, where it is the only statement of which job the number belongs to.
 */
export const GapsJobShortfalls: FC<Props> = ({ jobTotals, dots }) => (
	<>
		{jobTotals.map(({ key, jobLabel, actual, required }) => {
			const shortfall = shortfallOf({ actual, required });
			const isShort = isShownAsShort(shortfall);
			return (
				<Box
					// The section's key: two jobs of one name are two totals (`keySections`).
					key={key}
					component="span"
					data-testid={`gaps-metrics-job-${jobLabel}`}
					sx={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 0.5 }}
				>
					<ColorDot color={dots.get(jobLabel)} ariaLabel={jobLabel} />
					<Box
						component="span"
						sx={{
							fontSize: FONT_SIZE.SECONDARY,
							fontWeight: 700,
							fontVariantNumeric: "tabular-nums",
							color: isShort ? STATUS.ERROR.main : STATUS.SUCCESS.main,
						}}
					>
						{isShort ? `−${formatStaffCount(shortfall)}` : "✓"}
					</Box>
				</Box>
			);
		})}
	</>
);
